"use client";

import Circular from "@/components/Circular";
import CircularButton from "@/components/CircularButton";
import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import { shareOrCopy } from "@/utils/clipboard";
import { iconSizes } from "@/utils/iconProps";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { ExclusiveUserContent } from "@knowt/syncing/types/common";
import { isFolder } from "@knowt/syncing/utils/dataCleaning";
import { SxProps } from "@mui/material/styles";
import { Globe, KeyRound, Lock, Share } from "lucide-react";
import React, { useMemo } from "react";
import toast from "react-hot-toast";
import { useContentItemsSharingPopupContextSelector } from "./hooks/useContentItemsSharingContext";
import { assertTruthy } from "@knowt/syncing/utils/assertions";
import { ItemType } from "@knowt/syncing/graphql/schema";

const SharingPillWithHandler = ({
    media,
    note,
    flashcardSet,
    folder,
    assignment,
    sx,
    circularSx,
    iconColor,
    iconOnly,
    ...props
}: ExclusiveUserContent & {
    sx?: SxProps;
    circularSx?: Omit<SxProps, "backgroundColor"> & { backgroundColor?: string };
    iconColor?: string;
    iconOnly?: boolean;
} & React.HTMLAttributes<HTMLButtonElement>) => {
    const { userId, isUnder13, sharingEnabled } = useCurrentUser();
    const openContentItemsSharingPopup = useContentItemsSharingPopupContextSelector(
        state => state.openContentItemsSharingPopup
    );

    const item = media || note || flashcardSet || folder || assignment;

    const itemType = useMemo(() => {
        if (media) return ItemType.MEDIA;
        if (note) return ItemType.NOTE;
        if (flashcardSet) return ItemType.FLASHCARDSET;
        if (assignment) return ItemType.ASSIGNMENT;
        if (folder) return ItemType.FOLDER;
        assertTruthy(false, "itemType should be defined");
    }, [media, note, flashcardSet, folder, assignment]);

    const isPublic = item?.public;
    const isPasswordProtected = !!item?.password;

    const isOwner = item?.userId === userId;

    const handleShareClick = async () => {
        if (!sharingEnabled) {
            if (isUnder13) {
                return toast.error(
                    "Sorry, for privacy reasons, you can't change the sharing settings of this file if you are under 13!"
                );
            }
            return toast.error(
                "Sorry, you can't change the sharing settings of this file. Your organization has disabled this feature."
            );
        }

        if (isOwner) {
            openContentItemsSharingPopup({
                itemType: itemType,
                medias: media ? [media] : undefined,
                notes: note ? [note] : undefined,
                flashcardSets: flashcardSet ? [flashcardSet] : undefined,
                folders: folder ? [folder] : undefined,
                assignments: assignment ? [assignment] : undefined,
            });
        } else {
            await shareOrCopy({ title: (isFolder(item) ? item?.name : item?.title) || "Knowt" });
        }
    };

    if (iconOnly) {
        return (
            <CircularButton
                radius={"3.8rem"}
                style={{
                    backgroundColor: "transparent",
                    ...circularSx,
                }}
                onClick={handleShareClick}>
                {!isOwner ? (
                    <Share size={iconSizes.SM_S} color={iconColor ?? themeColors.neutralBlack} />
                ) : isPublic ? (
                    <Globe size={iconSizes.SM_S} color={iconColor ?? themeColors.neutralBlack} />
                ) : isPasswordProtected ? (
                    <KeyRound size={iconSizes.SM_S} color={iconColor ?? themeColors.neutralBlack} />
                ) : (
                    <Lock size={iconSizes.SM_S} color={iconColor ?? themeColors.neutralBlack} />
                )}
            </CircularButton>
        );
    }

    return (
        <CircularRectTextButton
            {...props}
            className="secondaryTextBold1"
            sx={{
                backgroundColor: themeColors.neutralWhite,
                border: `1px solid ${themeColors.neutral1}`,
                color: themeColors.neutralBlack,
                padding: "0.8rem 0.8rem",
                paddingRight: spacing.SM,
                gap: spacing.XS,
                height: "4rem",
                justifyContent: "flex-start",
                "&:hover": {
                    borderColor: themeColors.neutralBlack,
                },
                ...sx,
            }}
            onClick={handleShareClick}
            tooltip={"Edit sharing settings"}>
            <Circular
                radius={"3rem"}
                style={{
                    backgroundColor: "transparent",
                    ...circularSx,
                }}>
                {!isOwner ? (
                    <Share size={iconSizes.SM_S} color={iconColor ?? themeColors.neutralBlack} />
                ) : isPublic ? (
                    <Globe size={iconSizes.SM_S} color={iconColor ?? themeColors.neutralBlack} />
                ) : isPasswordProtected ? (
                    <KeyRound size={iconSizes.SM_S} color={iconColor ?? themeColors.neutralBlack} />
                ) : (
                    <Lock size={iconSizes.SM_S} color={iconColor ?? themeColors.neutralBlack} />
                )}
            </Circular>
            <p>{!isOwner ? "Share" : "Sharing settings"}</p>
        </CircularRectTextButton>
    );
};

export default SharingPillWithHandler;
